import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router'; // Import Router
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
// PrimeNG Modules
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { AvatarModule } from 'primeng/avatar';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { BadgeModule } from 'primeng/badge';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { TextareaModule } from 'primeng/textarea';
import { CalendarModule } from 'primeng/calendar';
import { SelectButtonModule } from 'primeng/selectbutton';
import { MessageService, LazyLoadEvent } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
// Models
import { AppUser } from '../../users/user.model';
import { Task } from './task.model';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { Resource } from '../../master-data/resource/resource.model';
import { ResourceService } from '../../master-data/resource/resource.service';
import { UserService } from '../../users/users.service';
import { TaskService } from './task.service';
import { AuthenticationService } from '../../services/authentication.service';
import { AppDatePipe } from '../../pipes/app-date.pipe';

@Component({
  selector: 'app-task-list',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule, InputTextModule,
    ButtonModule, DialogModule, AvatarModule, TableModule, BadgeModule, TagModule, SelectButtonModule,
    DropdownModule, TextareaModule, CalendarModule, ToastModule,
    InputGroupModule, InputGroupAddonModule, IftaLabelModule, FormsModule, AppDatePipe
  ],
  providers: [MessageService],
  templateUrl: './task-list.component.html',
  styleUrls: ['./task-list.component.css']
})
export class TaskListComponent implements OnInit {

  @Input() display: 'inline' | 'dialog' = 'dialog';
  @ViewChild('dt') table!: Table;

  visible: boolean = false;
  taskFormDialogVisible: boolean = false;

  tasks: Task[] = [];
  totalRecords: number = 0;
  loading: boolean = false;

  taskForm!: FormGroup;

  // Data for dropdowns
  currentUser: AppUser | null = null;
  users: AppUser[] = [];
  priorities: string[] = ['High', 'Medium', 'Low'];
  objectTypes: Resource[] = [];

  filter: 'assignedToMe' | 'requestedByMe' = 'assignedToMe';
  filterOptions: any[];

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private authService: AuthenticationService,
    private messageService: MessageService,
    private resourceService: ResourceService,
    private userService: UserService,
    private taskService: TaskService
  ) {
    this.filterOptions = [
      { label: 'Assigned To Me', value: 'assignedToMe' },
      { label: 'Requested By Me', value: 'requestedByMe' }
    ];
  }

  ngOnInit() {
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
      this.loadTasks();
    });

    this.loadDropdownData();
    this.taskForm = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      businessObjectType: [null, Validators.required],
      businessObjectId: [null, [Validators.required, Validators.pattern("^[0-9]*$")]],
      dueDate: [null, Validators.required],
      assignedTo: [null, Validators.required],
      priority: ['Medium', Validators.required]
    });
  }

  loadTasks(event?: TableLazyLoadEvent) {
    this.loading = true;
    if (!this.currentUser?.id) {
      this.tasks = [];
      this.totalRecords = 0;
      this.loading = false;
      return;
    }
    let params = new HttpParams();
    if (this.filter === 'assignedToMe') {
      params = params.set('assignedTo.id', this.currentUser.id.toString());
    } else if (this.filter === 'requestedByMe') {
      params = params.set('requestedBy.id', this.currentUser.id.toString());
    }

    let page = (event?.first ?? 0) / (event?.rows ?? 25);
    let size = event?.rows ?? 25;
    params = params.set('page', page.toString());
    params = params.set('size', size.toString());

    if (event && event.sortField) {
      params = params.set('sort', `${event.sortField},${event.sortOrder === 1 ? 'asc' : 'desc'}`);
    }

    this.taskService.getAllTasks(params).subscribe(page => {
      this.tasks = page.content;
      this.totalRecords = page.totalElements;
      this.loading = false;
    });
  }

  loadDropdownData() {
    this.userService.getAllUsers().subscribe(page => {
      this.users = page.content;
    });
    this.resourceService.getAllResources(new HttpParams()).subscribe(page => {
      this.objectTypes = page.content;
    });
  }

  showDialog() {
    this.visible = true;
  }

  openNewTaskDialog() {
    this.taskFormDialogVisible = true;
  }

  saveNewTask() {
    if (this.taskForm.invalid) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill all required fields.' });
      return;
    }
    const formValue = this.taskForm.value;
    const assignedUser = this.users.find(u => u.id === formValue.assignedTo);

    const newTaskDto: Partial<Task> = {
      title: formValue.title,
      description: formValue.description,
      businessObjectType: formValue.businessObjectType,
      businessObjectId: formValue.businessObjectId,
      dueDate: formValue.dueDate,
      priority: formValue.priority,
      assignedTo: assignedUser,
    };

    this.taskService.createTask(newTaskDto).subscribe({
      next: (createdTask) => {
        this.tasks.unshift(createdTask);
        this.taskFormDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Task created and assigned successfully.' });
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to create task.' });
        console.error(err);
      }
    });
  }

  navigateToTask(task: Task): void {
    if (task.routerLink) {
      this.router.navigate(task.routerLink);
      this.visible = false; // Close dialog on navigation
    }
  }

  getPrioritySeverity(priority: string): string {
    switch (priority) {
      case 'High':
        return 'danger';
      case 'Medium':
        return 'warn';
      case 'Low':
        return 'info';
      default:
        return 'secondary';
    }
  }

  getPendingCount(): number {
    return this.tasks.filter(task => task.isPending).length;
  }

  markAsCompleted(task: Task) {
    // update the task with status completed. If there is a workflowApprovalId value, then record the approval in the server for this ID too

    task.isPending = false; // Mark as completed locally

    this.taskService.updateTask(task.taskId, task).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: `Task "${task.title}" marked as completed.` });
        // Optionally, remove the task from the list or refresh the list
        this.tasks = this.tasks.filter(t => t.taskId !== task.taskId);
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to mark task as completed.' });
        console.error(err);
        task.isPending = true; // Revert if update fails
      }
    });

  }
}
